/**
 * JINA AI Service
 * Uses JINA AI API for web scraping and review extraction
 */

interface JinaAIConfig {
  apiKey: string;
  baseUrl: string;
}

interface ReviewData {
  source: string;
  rating: number;
  reviewCount: number;
  reviews: {
    author: string;
    rating: number;
    text: string;
    date: string;
  }[];
}

interface ScrapedContent {
  title: string;
  content: string;
  metadata: {
    description?: string;
    keywords?: string[];
    author?: string;
  };
}

class JinaAIService {
  private config: JinaAIConfig;

  constructor() {
    this.config = {
      apiKey: process.env.JINA_API_KEY || '',
      baseUrl: 'https://r.jina.ai',
    };
  }

  /**
   * Scrape a URL using JINA AI
   */
  async scrapeURL(url: string): Promise<ScrapedContent | null> {
    try {
      const response = await fetch(`${this.config.baseUrl}/${url}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.config.apiKey}`,
          'X-Return-Format': 'markdown',
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`JINA AI API error: ${response.status}`);
      }

      const data = await response.json();

      return {
        title: data.title || '',
        content: data.content || '',
        metadata: {
          description: data.description,
          keywords: data.keywords,
          author: data.author,
        },
      };
    } catch (error) {
      console.error('Error scraping URL with JINA AI:', error);
      return null;
    }
  }

  /**
   * Extract reviews from Google search
   */
  async scrapeGoogleReviews(businessName: string, location: string): Promise<ReviewData | null> {
    try {
      const searchQuery = encodeURIComponent(`${businessName} ${location} reviews`);
      const googleUrl = `https://www.google.com/search?q=${searchQuery}`;

      const content = await this.scrapeURL(googleUrl);

      if (!content) {
        return null;
      }

      // Parse reviews from content
      const reviews = this.parseReviewsFromContent(content.content);

      return {
        source: 'Google',
        rating: reviews.averageRating,
        reviewCount: reviews.count,
        reviews: reviews.items,
      };
    } catch (error) {
      console.error('Error scraping Google reviews:', error);
      return null;
    }
  }

  /**
   * Extract reviews from Yelp
   */
  async scrapeYelpReviews(businessName: string, location: string): Promise<ReviewData | null> {
    try {
      const searchQuery = encodeURIComponent(`${businessName} ${location}`);
      const yelpUrl = `https://www.yelp.com/search?find_desc=${searchQuery}`;

      const content = await this.scrapeURL(yelpUrl);

      if (!content) {
        return null;
      }

      const reviews = this.parseYelpReviews(content.content);

      return {
        source: 'Yelp',
        rating: reviews.averageRating,
        reviewCount: reviews.count,
        reviews: reviews.items,
      };
    } catch (error) {
      console.error('Error scraping Yelp reviews:', error);
      return null;
    }
  }

  /**
   * Get aggregated reviews from multiple sources
   */
  async getAggregatedReviews(businessName: string, location: string): Promise<{
    averageRating: number;
    totalReviews: number;
    sources: ReviewData[];
  }> {
    const sources: ReviewData[] = [];

    // Scrape Google reviews
    const googleReviews = await this.scrapeGoogleReviews(businessName, location);
    if (googleReviews) {
      sources.push(googleReviews);
    }

    // Scrape Yelp reviews
    const yelpReviews = await this.scrapeYelpReviews(businessName, location);
    if (yelpReviews) {
      sources.push(yelpReviews);
    }

    // Calculate weighted average
    let totalRating = 0;
    let totalCount = 0;

    sources.forEach(source => {
      totalRating += source.rating * source.reviewCount;
      totalCount += source.reviewCount;
    });

    const averageRating = totalCount > 0 ? totalRating / totalCount : 0;

    return {
      averageRating: Math.round(averageRating * 10) / 10,
      totalReviews: totalCount,
      sources,
    };
  }

  /**
   * Parse reviews from scraped content
   */
  private parseReviewsFromContent(content: string): {
    averageRating: number;
    count: number;
    items: any[];
  } {
    // Extract rating patterns
    const ratingPattern = /(\d+\.?\d*)\s*(stars?|★)/gi;
    const ratings: number[] = [];

    let match;
    while ((match = ratingPattern.exec(content)) !== null) {
      const rating = parseFloat(match[1]);
      if (rating >= 1 && rating <= 5) {
        ratings.push(rating);
      }
    }

    // Extract review count patterns
    const countPattern = /(\d+)\s*reviews?/gi;
    let reviewCount = 0;
    const countMatch = countPattern.exec(content);
    if (countMatch) {
      reviewCount = parseInt(countMatch[1]);
    }

    // Calculate average
    const averageRating = ratings.length > 0
      ? ratings.reduce((sum, r) => sum + r, 0) / ratings.length
      : 0;

    return {
      averageRating: Math.round(averageRating * 10) / 10,
      count: reviewCount || ratings.length,
      items: [],
    };
  }

  /**
   * Parse Yelp-specific reviews
   */
  private parseYelpReviews(content: string): {
    averageRating: number;
    count: number;
    items: any[];
  } {
    return this.parseReviewsFromContent(content);
  }

  /**
   * Scrape content for SEO generation
   */
  async scrapeForSEO(url: string): Promise<{
    title: string;
    description: string;
    keywords: string[];
    content: string;
  } | null> {
    try {
      const scraped = await this.scrapeURL(url);

      if (!scraped) {
        return null;
      }

      return {
        title: scraped.title,
        description: scraped.metadata.description || '',
        keywords: scraped.metadata.keywords || [],
        content: scraped.content,
      };
    } catch (error) {
      console.error('Error scraping for SEO:', error);
      return null;
    }
  }

  /**
   * Batch scrape multiple URLs
   */
  async batchScrape(urls: string[]): Promise<Map<string, ScrapedContent>> {
    const results = new Map<string, ScrapedContent>();

    for (const url of urls) {
      try {
        const content = await this.scrapeURL(url);
        if (content) {
          results.set(url, content);
        }

        // Rate limiting - wait 1 second between requests
        await new Promise(resolve => setTimeout(resolve, 1000));
      } catch (error) {
        console.error(`Error scraping ${url}:`, error);
      }
    }

    return results;
  }

  /**
   * Get business information from website
   */
  async scrapeBusinessInfo(websiteUrl: string): Promise<{
    description: string;
    products: string[];
    hours: any;
    contact: {
      phone?: string;
      email?: string;
      address?: string;
    };
  } | null> {
    try {
      const content = await this.scrapeURL(websiteUrl);

      if (!content) {
        return null;
      }

      // Extract business info from content
      return this.parseBusinessInfo(content.content);
    } catch (error) {
      console.error('Error scraping business info:', error);
      return null;
    }
  }

  /**
   * Parse business information from content
   */
  private parseBusinessInfo(content: string): {
    description: string;
    products: string[];
    hours: any;
    contact: {
      phone?: string;
      email?: string;
      address?: string;
    };
  } {
    // Extract phone numbers
    const phonePattern = /(\+?1[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}/g;
    const phones = content.match(phonePattern) || [];

    // Extract email addresses
    const emailPattern = /[\w.-]+@[\w.-]+\.\w+/g;
    const emails = content.match(emailPattern) || [];

    // Extract hours (simple pattern)
    const hoursPattern = /(monday|tuesday|wednesday|thursday|friday|saturday|sunday)[\s:]+(\d{1,2}:\d{2}\s*(?:am|pm)?)\s*-\s*(\d{1,2}:\d{2}\s*(?:am|pm)?)/gi;
    const hours: any = {};
    let match;
    while ((match = hoursPattern.exec(content)) !== null) {
      hours[match[1].toLowerCase()] = `${match[2]} - ${match[3]}`;
    }

    return {
      description: content.substring(0, 500),
      products: [],
      hours,
      contact: {
        phone: phones[0],
        email: emails[0],
      },
    };
  }
}

export default new JinaAIService();
